import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../../apiFetch';
import './Header.css';

const normalizeLink = (link) => {
  if (!link) return "";
  if (/^https?:\/\//i.test(link)) return link;
  return `${window.location.origin}${link.startsWith("/") ? "" : "/"}${link}`;
};

const Header = ({ profileImage, onLogout, userId }) => {
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  // 🔑 NEW: gate sound playback
  const [canPlaySound, setCanPlaySound] = useState(false);

  // ✅ NEW: local fallback for broken header profile image
  const [imageFailed, setImageFailed] = useState(false);

  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const prevUnreadRef = useRef(0);
  const navigate = useNavigate();

  // ✅ Reset broken-image state whenever profileImage changes
  useEffect(() => {
    setImageFailed(false);
  }, [profileImage]);

  /* ===========================
     🔓 ENABLE SOUND AFTER USER INTERACTION
  =========================== */
  useEffect(() => {
    const enableSound = () => setCanPlaySound(true);
    window.addEventListener('click', enableSound, { once: true });
    return () => window.removeEventListener('click', enableSound);
  }, []);

  /* ===========================
     🔔 FETCH NOTIFICATIONS
  =========================== */
  const fetchNotifications = async () => {
    if (!userId) return;

    try {
      const res = await apiFetch(`/api/notifications/player/${userId}`);

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Failed to fetch notifications (${res.status}): ${text}`);
      }

      const data = await res.json();

      const arr = Array.isArray(data)
        ? data
        : data?.items || data?.data || data?.notifications || [];

      const unreadCount =
        data?.unreadCount ??
        arr.filter(n => !n.isRead).length;

      // 🔔 Play sound ONLY if user interacted
      if (unreadCount > prevUnreadRef.current && canPlaySound) {
        const audio = new Audio('/ringtone-you-would-be-glad-to-know.mp3');
        audio.play().catch(() => { });
      }

      prevUnreadRef.current = unreadCount; // ✅ update ref
      setNotificationCount(unreadCount);

      setNotifications(
        arr.slice().sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      );
    } catch (err) {
      console.error('❌ Error fetching notifications:', err);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [userId, canPlaySound]);

  /* ===========================
     👤 PROFILE DROPDOWN
  =========================== */
  const handleProfileClick = (e) => {
    e.stopPropagation();
    setDropdownVisible(prev => !prev);
  };

  /* ===========================
     🏆 LEADERBOARD
  =========================== */
  const handleLeaderboardClick = () => {
    navigate("/player-leaderboard");
  };

  /* ===========================
     🔔 NOTIFICATION BELL
  =========================== */
  const handleNotificationClick = async (e) => {
    e.stopPropagation();
    setShowNotifications(prev => !prev);

    if (notificationCount > 0) {
      try {
        const res = await apiFetch(`/api/notifications/player/${userId}/read`, {
          method: "PUT",
        });

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(`Failed to mark notifications as read (${res.status}): ${text}`);
        }

        setNotificationCount(0);
        prevUnreadRef.current = 0; // ✅ reset unread ref too
        fetchNotifications();
      } catch (err) {
        console.error('❌ Error marking notifications as read:', err);
      }
    }
  };

  const handleNotificationItemClick = (note) => {
    navigate('/player-notifications', {
      state: { notification: note },
    });
    setShowNotifications(false);
  };

  /* ===========================
     🧼 CLICK OUTSIDE HANDLER
  =========================== */
  const handleClickOutside = (event) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target)
    ) {
      setDropdownVisible(false);
    }

    if (
      notificationRef.current &&
      !notificationRef.current.contains(event.target) &&
      !event.target.closest('.notification-bell-container')
    ) {
      setShowNotifications(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () =>
      document.removeEventListener('click', handleClickOutside);
  }, []);

  /* ===========================
     🧱 RENDER
  =========================== */
  return (
    <header className="player-dashboard-header">
      <div className="logo-container">
        <img
          src="/images/VSN.png"
          alt="Logo"
          className="logo-image"
        />
      </div>

      <div className="title">
        Virtual Scouting Network (Player Dashboard)
      </div>

      {/* 🏆 + 🔔 + 👤 Header Icons */}
      <div className="header-icons">

        {/* 🏆 Leaderboard */}
        <div
          className="leaderboard-icon"
          onClick={handleLeaderboardClick}
          title="Leaderboard"
        >
          🏆
        </div>

        {/* 🔔 Notification Bell */}
        <div
          className="notification-bell-container"
          onClick={handleNotificationClick}
        >
          <span className="notification-bell">
            🔔
            {notificationCount > 0 && (
              <span className="notification-count">
                {notificationCount}
              </span>
            )}
          </span>

          {showNotifications && notifications.length > 0 && (
            <div
              className="notification-dropdown"
              ref={notificationRef}
            >
              <ul>
                {notifications.map((note) => (
                  <li
                    key={note.id}
                    className={note.isRead ? "read" : "unread"}
                    onClick={() => handleNotificationItemClick(note)}
                    style={{ cursor: "pointer" }}
                  >
                    <strong className="notification-title">
                      {note.type || "Notification"}
                    </strong>

                    <br />
                    {note.message}

                    {/* ✅ SHOW LINK IF IT EXISTS */}
                    {note.link && (
                      <>
                        <br />
                        <a
                          href={normalizeLink(note.link)}
                          target="_blank"
                          rel="noreferrer"
                          className="header-notification-link"
                          onClick={(e) => e.stopPropagation()} // ✅ prevent opening notifications page
                        >
                          {note.linkLabel || "View"}
                        </a>
                      </>
                    )}

                    <br />
                    <span style={{ fontSize: "11px", color: "gray" }}>
                      {new Date(note.timestamp).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* 👤 Profile Image + Logout Dropdown */}
        <div
          className="profile-container"
          onClick={handleProfileClick}
          ref={dropdownRef}
        >
          {profileImage && !imageFailed ? (
            <img
              src={profileImage}
              alt="Profile"
              className="header-profile-image"
              onError={() => {
                console.error("Failed to load header profile image:", profileImage);
                setImageFailed(true);
              }}
            />
          ) : (
            <span className="default-profile"></span>
          )}

          {isDropdownVisible && (
            <div className="dropdown-menu">
              <ul>
                <li
                  onClick={(e) => {
                    e.stopPropagation();
                    onLogout();
                  }}
                >
                  Logout
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;




